import React, { useState } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ClockCircleOutlined } from '@ant-design/icons';

import { partialEditLeadTaskRequest } from '@/services/api/leadTask';
import { processAPIResponseNow } from '@/utils/request';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import { buildSnoozeUntil, isTaskSnoozed, SNOOZE_PRESETS } from '@/utils/task';
import { F } from '@/utils/intl';

import styles from './TaskSnooze.less';

type IUseTaskSnoozeArgs = {
  task: LeadTaskModel;
  updateRequest: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

/**
 * Snooze affordance for a task: a "Snooze" dropdown submenu (presets +
 * custom date + remove). "Custom…" opens the DatePicker's own calendar as a
 * popover anchored at the actions — no modal. The consumer spreads
 * `snoozeMenuItem` into its dropdown items and renders `snoozePopover`.
 */
export const useTaskSnooze = ({ task, updateRequest, onLoadingChange }: IUseTaskSnoozeArgs) => {
  const [customOpen, setCustomOpen] = useState(false);

  const applySnooze = (snoozeUntil: string | null) => {
    onLoadingChange?.(true);
    partialEditLeadTaskRequest({
      params: { taskId: task.id },
      data: { snooze_until: snoozeUntil },
    }).then((response) => {
      onLoadingChange?.(false);
      processAPIResponseNow(response, {
        onSuccess: () => updateRequest(),
      });
    }).catch(() => {
      onLoadingChange?.(false);
    });
  };

  const snoozed = isTaskSnoozed(task);

  const snoozeMenuItem = {
    key: 'snooze',
    icon: <ClockCircleOutlined />,
    label: <F id="pages.tasks.snooze" defaultMessage="Snooze" />,
    children: [
      {
        key: 'snooze-1d',
        label: <F id="pages.tasks.snoozeOneDay" defaultMessage="1 day" />,
        onClick: () => applySnooze(buildSnoozeUntil(SNOOZE_PRESETS.ONE_DAY)),
      },
      {
        key: 'snooze-3d',
        label: <F id="pages.tasks.snoozeThreeDays" defaultMessage="3 days" />,
        onClick: () => applySnooze(buildSnoozeUntil(SNOOZE_PRESETS.THREE_DAYS)),
      },
      {
        key: 'snooze-1w',
        label: <F id="pages.tasks.snoozeOneWeek" defaultMessage="1 week" />,
        onClick: () => applySnooze(buildSnoozeUntil(SNOOZE_PRESETS.ONE_WEEK)),
      },
      {
        key: 'snooze-custom',
        label: <F id="pages.tasks.snoozeCustom" defaultMessage="Custom…" />,
        onClick: () => setCustomOpen(true),
      },
      ...(snoozed
        ? [
            { type: 'divider' as const },
            {
              key: 'snooze-clear',
              label: <F id="pages.tasks.clearSnooze" defaultMessage="Remove snooze" />,
              onClick: () => applySnooze(null),
            },
          ]
        : []),
    ],
  };

  const handlePicked = (date: Dayjs | null) => {
    setCustomOpen(false);
    if (date) applySnooze(buildSnoozeUntil(date));
  };

  // An invisible anchor whose calendar popup acts as the "popover". The
  // wrapper stops propagation so portal clicks never reach the row's
  // open-edit-drawer handler behind the picker.
  const snoozePopover = customOpen ? (
    <span className={styles.pickerAnchor} onClick={(e) => e.stopPropagation()}>
      <DatePicker
        open
        showTime={{ format: 'HH:mm' }}
        // The API silently drops past timestamps — don't offer them at all;
        // snoozing later today is valid (it's a timestamp, not a date)
        disabledDate={(date) => date.isBefore(dayjs(), 'day')}
        disabledTime={(date) => {
          if (!date || !date.isSame(dayjs(), 'day')) return {};
          const now = dayjs();
          return {
            disabledHours: () => Array.from({ length: now.hour() }, (_, hour) => hour),
            disabledMinutes: (hour: number) =>
              hour === now.hour() ? Array.from({ length: now.minute() + 1 }, (_, minute) => minute) : [],
          };
        }}
        onOk={handlePicked}
        onOpenChange={(open) => {
          if (!open) setCustomOpen(false);
        }}
        variant="borderless"
        suffixIcon={null}
        allowClear={false}
        inputReadOnly
      />
    </span>
  ) : null;

  return { snoozeMenuItem, snoozePopover };
};

export default useTaskSnooze;
